import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../../components/Component";
import { Form, Badge } from "reactstrap";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const MAX_TOTAL_UPLOAD_MB = 20;
const MAX_TOTAL_UPLOAD_BYTES = MAX_TOTAL_UPLOAD_MB * 1024 * 1024;

const ObservationFormPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = !!id;
  const { register, handleSubmit, errors, setValue } = useForm();

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotStatus, setCopilotStatus] = useState(null);
  const [copilotSuggestion, setCopilotSuggestion] = useState(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    type: "positive",
    category: "",
    description: "",
    severity: "low",
    status: "submitted",
    site_id: "",
    risk_potential: "",
    equipment_concerned: "",
    people_exposed: "",
    number_of_people_exposed: "",
    exposure_duration: "",
    immediate_action_taken: false,
    improvement_proposal: "",
    correction_due_date: "",
    estimated_cost: "",
    location_latitude: "",
    location_longitude: "",
    location_description: "",
    weather_conditions: "",
    date_observation: "",
    assigned_to: "",
  });

  useEffect(() => {
    loadSites();
    loadUsers();
    loadCopilotStatus();
    if (isEdit) {
      loadObservation();
    }
  }, [id]);

  // Gérer les URLs de preview et les nettoyer
  useEffect(() => {
    const urls = {};
    attachments.forEach((file, index) => {
      if (isImage(file) || isVideo(file)) {
        urls[index] = URL.createObjectURL(file);
      }
    });
    setPreviewUrls(urls);

    // Nettoyer les URLs lors du démontage ou changement de fichiers
    return () => {
      Object.values(urls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [attachments]);

  const loadSites = async () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.sites && user.sites.length > 0) {
          setSites(user.sites);
          return;
        }
      }
      try {
        const response = await api.getSites();
        setSites(response.data || response || []);
      } catch (error) {
        try {
          const meResponse = await api.me();
          if (meResponse.sites && meResponse.sites.length > 0) {
            setSites(meResponse.sites);
          }
        } catch (meError) {
          toast.error("Erreur lors du chargement des sites");
        }
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des sites");
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ per_page: 100 });
      setUsers(response.data || response || []);
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs:", error);
    }
  };

  const loadCopilotStatus = async () => {
    try {
      const status = await api.getObservationCopilotStatus();
      setCopilotStatus(status);
    } catch (error) {
      setCopilotStatus({ configured: false });
    }
  };

  const setFormFieldValue = (name, value) => {
    setValue(name, value, { shouldDirty: true, shouldValidate: false });
    const formEl = formRef.current;
    if (!formEl) return;
    const input = formEl.querySelector(`[name="${name}"]`);
    if (!input) return;
    input.value = value ?? "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const applyCopilotSuggestion = (suggestion) => {
    if (!suggestion || typeof suggestion !== "object") return;

    const fieldMap = {
      title: suggestion.title,
      type: suggestion.type,
      category: suggestion.category,
      severity: suggestion.severity,
      risk_potential: suggestion.risk_potential,
      improvement_proposal: suggestion.improvement_proposal,
    };

    Object.entries(fieldMap).forEach(([field, value]) => {
      if (typeof value === "string" && value.trim() !== "") {
        setFormFieldValue(field, value);
      }
    });
  };

  const handleCopilotSuggest = async () => {
    const formEl = formRef.current;
    if (!formEl) return;

    const getRaw = (name) => formEl.querySelector(`[name="${name}"]`)?.value || "";
    const description = String(getRaw("description")).trim();

    if (description.length < 20) {
      toast.warning("Ajoute au moins 20 caractères dans la description pour utiliser le copilote IA.");
      return;
    }

    setCopilotLoading(true);
    try {
      const payload = {
        title: String(getRaw("title")).trim() || null,
        description,
        type: getRaw("type") || null,
        category: getRaw("category") || null,
        severity: getRaw("severity") || null,
        risk_potential: String(getRaw("risk_potential")).trim() || null,
        improvement_proposal: String(getRaw("improvement_proposal")).trim() || null,
        site_id: getRaw("site_id") ? parseInt(getRaw("site_id"), 10) : null,
      };
      const suggestion = await api.postObservationCopilotSuggest(payload);
      setCopilotSuggestion(suggestion);
      applyCopilotSuggestion(suggestion);
      toast.success("Suggestions IA appliquées au formulaire.");
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || "Copilote IA indisponible.";
      toast.error(msg);
    } finally {
      setCopilotLoading(false);
    }
  };

  const loadObservation = async () => {
    try {
      setLoading(true);
      const observation = await api.getObservation(id);
      const obs = observation.data || observation;
      
      // Formater la date d'observation si elle existe
      let dateObservation = "";
      if (obs.date_observation) {
        const date = new Date(obs.date_observation);
        dateObservation = date.toISOString().slice(0, 16);
      } else if (obs.created_at) {
        const date = new Date(obs.created_at);
        dateObservation = date.toISOString().slice(0, 16);
      }

      // Formater la date de correction si elle existe
      let correctionDate = "";
      if (obs.correction_due_date) {
        const date = new Date(obs.correction_due_date);
        correctionDate = date.toISOString().split('T')[0];
      }

      setFormData({
        title: obs.title || "",
        type: obs.type || "positive",
        category: obs.category || "",
        description: obs.description || "",
        severity: obs.severity || "low",
        status: obs.status || "submitted",
        site_id: obs.site_id || "",
        risk_potential: obs.risk_potential || "",
        equipment_concerned: obs.equipment_concerned || "",
        people_exposed: obs.people_exposed || "",
        number_of_people_exposed: obs.number_of_people_exposed || "",
        exposure_duration: obs.exposure_duration || "",
        immediate_action_taken: obs.immediate_action_taken || false,
        improvement_proposal: obs.improvement_proposal || "",
        correction_due_date: correctionDate,
        estimated_cost: obs.estimated_cost || "",
        location_latitude: obs.location_latitude || "",
        location_longitude: obs.location_longitude || "",
        location_description: obs.location_description || "",
        weather_conditions: obs.weather_conditions || "",
        date_observation: dateObservation,
        assigned_to: obs.assigned_to?.id || obs.assigned_to || "",
      });

      // Définir les valeurs pour react-hook-form
      Object.keys(formData).forEach(key => {
        setValue(key, formData[key]);
      });
    } catch (error) {
      toast.error("Erreur lors du chargement de l'observation");
      history.push("/observations");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length > 0) {
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "mp4", "avi", "mov", "wmv", "flv", "webm"];
      const invalidFiles = newFiles.filter((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.includes(ext);
      });
      if (invalidFiles.length > 0) {
        toast.warning("Certains fichiers ont été ignorés (format non autorisé).");
      }
      const validFiles = newFiles.filter((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return ext && allowedExtensions.includes(ext);
      });
      if (validFiles.length === 0) {
        e.target.value = '';
        return;
      }
      // Ajouter les nouveaux fichiers aux fichiers existants (pas de remplacement)
      setAttachments(prevAttachments => {
        // Éviter les doublons en vérifiant le nom et la taille
        const existingFiles = prevAttachments.map(f => `${f.name}-${f.size}`);
        const uniqueNewFiles = validFiles.filter(f => !existingFiles.includes(`${f.name}-${f.size}`));
        const nextAttachments = [...prevAttachments, ...uniqueNewFiles];
        const totalSize = nextAttachments.reduce((sum, file) => sum + (file.size || 0), 0);
        if (totalSize > MAX_TOTAL_UPLOAD_BYTES) {
          toast.error(`La taille totale des fichiers ne doit pas dépasser ${MAX_TOTAL_UPLOAD_MB} Mo.`);
          return prevAttachments;
        }
        return nextAttachments;
      });
    }
    // Réinitialiser l'input pour permettre de sélectionner les mêmes fichiers à nouveau si nécessaire
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const getFilePreview = (file, index) => {
    return previewUrls[index] || null;
  };

  const isImage = (file) => {
    return file.type && file.type.startsWith('image/');
  };

  const isVideo = (file) => {
    return file.type && file.type.startsWith('video/');
  };

  const onFormSubmit = async (submitData) => {
    try {
      setLoading(true);
      setUploadProgress(0);
      
      const formattedData = {
        title: (submitData.title || "").trim(),
        type: submitData.type || "positive",
        category: submitData.category || "",
        description: (submitData.description || "").trim(),
        severity: submitData.severity || "low",
        status: submitData.status || "submitted",
        site_id: submitData.site_id && submitData.site_id !== "" ? parseInt(submitData.site_id, 10) : null,
        risk_potential: submitData.risk_potential || "",
        equipment_concerned: submitData.equipment_concerned || "",
        people_exposed: submitData.people_exposed || "",
        number_of_people_exposed: submitData.number_of_people_exposed ? parseInt(submitData.number_of_people_exposed, 10) : null,
        exposure_duration: submitData.exposure_duration || "",
        immediate_action_taken: submitData.immediate_action_taken === true || submitData.immediate_action_taken === "true" || submitData.immediate_action_taken === "1",
        improvement_proposal: submitData.improvement_proposal || "",
        correction_due_date: submitData.correction_due_date || null,
        estimated_cost: submitData.estimated_cost ? parseFloat(submitData.estimated_cost) : null,
        location_latitude: submitData.location_latitude ? parseFloat(submitData.location_latitude) : null,
        location_longitude: submitData.location_longitude ? parseFloat(submitData.location_longitude) : null,
        location_description: submitData.location_description || "",
        weather_conditions: submitData.weather_conditions || "",
        date_observation: submitData.date_observation || null,
        assigned_to: submitData.assigned_to && submitData.assigned_to !== "" ? parseInt(submitData.assigned_to, 10) : null,
      };

      if (!formattedData.site_id || formattedData.site_id === 0 || isNaN(formattedData.site_id)) {
        toast.error("Le site est requis");
        setLoading(false);
        return;
      }
      if (!formattedData.title || formattedData.title.trim() === "") {
        toast.error("Le titre est requis");
        setLoading(false);
        return;
      }
      if (!formattedData.type || formattedData.type.trim() === "") {
        toast.error("Le type est requis");
        setLoading(false);
        return;
      }
      if (!formattedData.description || formattedData.description.trim() === "") {
        toast.error("La description est requise");
        setLoading(false);
        return;
      }

      if (attachments.length > 0) {
        formattedData.attachments = attachments;
      }

      if (isEdit) {
        await api.updateObservation(id, formattedData, {
          onUploadProgress: (percent) => setUploadProgress(percent),
        });
        toast.success("Observation mise à jour avec succès");
      } else {
        await api.createObservation(formattedData, {
          onUploadProgress: (percent) => setUploadProgress(percent),
        });
        toast.success("Observation créée avec succès");
      }
      
      history.push("/observations");
    } catch (error) {
      let errorMessage = "Erreur lors de l'enregistrement de l'observation";
      
      if (error.errors) {
        const errorsList = Object.values(error.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errorsList = Object.values(error.response.data.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  if (loading && isEdit) {
    return (
      <React.Fragment>
        <Head title={isEdit ? "Modifier observation" : "Nouvelle observation"}></Head>
        <Content>
          <Block>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3 text-soft">Chargement des données...</p>
            </div>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title={isEdit ? "Modifier observation" : "Nouvelle observation"}></Head>
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle page>{isEdit ? "Modifier l'observation" : "Nouvelle observation"}</BlockTitle>
              </BlockHeadContent>
              <BlockHeadContent>
                <Button
                  color="light"
                  outline
                  onClick={() => history.push("/observations")}
                >
                  <Icon name="arrow-left"></Icon>
                  <span>Retour</span>
                </Button>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <div className="nk-block">
            <div className="card">
              <div className="card-inner">
                <Form className="is-alter" onSubmit={handleSubmit(onFormSubmit)} innerRef={formRef}>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Titre *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                          name="title"
                          defaultValue={formData.title}
                          ref={register({ required: "Le titre est requis" })}
                        />
                        {errors.title && (
                          <div className="invalid-feedback d-block">{errors.title.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Site *</label>
                        <select
                          className={`form-select ${errors.site_id ? 'is-invalid' : ''}`}
                          name="site_id"
                          defaultValue={formData.site_id || ""}
                          ref={register({ 
                            required: "Le site est requis",
                            validate: (value) => value !== "" || "Le site est requis"
                          })}
                        >
                          <option value="">Sélectionner un site</option>
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.name}
                            </option>
                          ))}
                        </select>
                        {errors.site_id && (
                          <div className="invalid-feedback d-block">{errors.site_id.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Type *</label>
                        <select
                          className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                          name="type"
                          defaultValue={formData.type}
                          ref={register({ required: "Le type est requis" })}
                        >
                          <option value="positive">Observation positive</option>
                          <option value="dangerous">Situation dangereuse</option>
                          <option value="near_miss">Presqu'accident</option>
                          <option value="non_conformity">Non-conformité</option>
                        </select>
                        {errors.type && (
                          <div className="invalid-feedback d-block">{errors.type.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Catégorie</label>
                        <select className="form-select" name="category" defaultValue={formData.category}>
                          <option value="">Sélectionner une catégorie</option>
                          <option value="safety">Sécurité</option>
                          <option value="environment">Environnement</option>
                          <option value="quality">Qualité</option>
                          <option value="health">Santé</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Description *</label>
                        <textarea
                          className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                          name="description"
                          rows="4"
                          defaultValue={formData.description}
                          ref={register({ required: "La description est requise" })}
                        />
                        {errors.description && (
                          <div className="invalid-feedback d-block">{errors.description.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <Button
                          type="button"
                          color="primary"
                          outline
                          size="sm"
                          onClick={handleCopilotSuggest}
                          disabled={copilotLoading || copilotStatus?.configured === false}
                        >
                          {copilotLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Suggestion IA...
                            </>
                          ) : (
                            <>
                              <Icon name="cpu" className="me-1"></Icon>
                              Suggérer avec IA
                            </>
                          )}
                        </Button>
                        {copilotStatus?.configured === false && (
                          <span className="text-warning small">IA non configurée côté serveur (GROQ_API_KEY).</span>
                        )}
                      </div>
                      {copilotSuggestion && (
                        <div className="alert alert-pro alert-info mt-2 mb-0">
                          <div className="fw-medium mb-1">Copilote IA appliqué</div>
                          <div className="small text-soft">
                            Confiance: {Math.round((copilotSuggestion.confidence || 0) * 100)}%
                            {copilotSuggestion.model ? ` · Modèle: ${copilotSuggestion.model}` : ""}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Gravité</label>
                        <select className="form-select" name="severity" defaultValue={formData.severity}>
                          <option value="low">Faible</option>
                          <option value="medium">Moyenne</option>
                          <option value="high">Élevée</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Date et heure de l'observation</label>
                        <input
                          type="datetime-local"
                          className={`form-control ${errors.date_observation ? 'is-invalid' : ''}`}
                          name="date_observation"
                          defaultValue={formData.date_observation}
                          ref={register({
                            validate: (value) => {
                              if (!value || value.trim() === "") return true; // Champ optionnel
                              // Vérifier que le format contient bien la date ET l'heure (format: YYYY-MM-DDTHH:mm)
                              const datetimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
                              if (!datetimePattern.test(value)) {
                                return "Veuillez saisir à la fois la date et l'heure (format: JJ/MM/AAAA HH:mm)";
                              }
                              // Vérifier que la date est valide
                              const date = new Date(value);
                              if (isNaN(date.getTime())) {
                                return "Veuillez saisir une date et heure valides";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.date_observation && (
                          <div className="invalid-feedback d-block">{errors.date_observation.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Potentiel de risque</label>
                        <textarea
                          className="form-control"
                          name="risk_potential"
                          rows="2"
                          defaultValue={formData.risk_potential}
                          placeholder="Décrire le potentiel de risque..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Équipement concerné</label>
                        <input
                          type="text"
                          className="form-control"
                          name="equipment_concerned"
                          defaultValue={formData.equipment_concerned}
                          placeholder="Nom de l'équipement concerné..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Conditions météorologiques</label>
                        <select className="form-select" name="weather_conditions" defaultValue={formData.weather_conditions}>
                          <option value="">Sélectionner</option>
                          <option value="sunny">Ensoleillé</option>
                          <option value="cloudy">Nuageux</option>
                          <option value="rainy">Pluvieux</option>
                          <option value="stormy">Orageux</option>
                          <option value="windy">Venteux</option>
                          <option value="foggy">Brumeux</option>
                          <option value="dry">Sec</option>
                          <option value="humid">Humide</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Personnes exposées</label>
                        <input
                          type="text"
                          className="form-control"
                          name="people_exposed"
                          defaultValue={formData.people_exposed}
                          placeholder="Décrire les personnes exposées..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Nombre de personnes exposées</label>
                        <input
                          type="number"
                          className={`form-control ${errors.number_of_people_exposed ? 'is-invalid' : ''}`}
                          name="number_of_people_exposed"
                          min="0"
                          defaultValue={formData.number_of_people_exposed}
                          placeholder="0"
                          ref={register({
                            validate: (value) => {
                              if (!value || value === "") return true; // Champ optionnel
                              const num = parseInt(value, 10);
                              if (isNaN(num) || num < 0) {
                                return "Le nombre doit être un entier positif";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.number_of_people_exposed && (
                          <div className="invalid-feedback d-block">{errors.number_of_people_exposed.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Durée d'exposition</label>
                        <select className="form-select" name="exposure_duration" defaultValue={formData.exposure_duration}>
                          <option value="">Sélectionner</option>
                          <option value="momentary">Momentanée</option>
                          <option value="few_minutes">Quelques minutes</option>
                          <option value="few_hours">Quelques heures</option>
                          <option value="full_day">Journée complète</option>
                          <option value="several_days">Plusieurs jours</option>
                          <option value="ongoing">En cours</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Assigné à</label>
                        <select className="form-select" name="assigned_to" defaultValue={formData.assigned_to}>
                          <option value="">Sélectionner un utilisateur</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Localisation - Latitude</label>
                        <input
                          type="number"
                          className={`form-control ${errors.location_latitude ? 'is-invalid' : ''}`}
                          name="location_latitude"
                          step="any"
                          defaultValue={formData.location_latitude}
                          placeholder="Ex: -4.2634"
                          ref={register({
                            validate: (value) => {
                              if (!value || value === "") return true; // Champ optionnel
                              const num = parseFloat(value);
                              if (isNaN(num) || num < -90 || num > 90) {
                                return "La latitude doit être entre -90 et 90";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.location_latitude && (
                          <div className="invalid-feedback d-block">{errors.location_latitude.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Localisation - Longitude</label>
                        <input
                          type="number"
                          className={`form-control ${errors.location_longitude ? 'is-invalid' : ''}`}
                          name="location_longitude"
                          step="any"
                          defaultValue={formData.location_longitude}
                          placeholder="Ex: 15.2429"
                          ref={register({
                            validate: (value) => {
                              if (!value || value === "") return true; // Champ optionnel
                              const num = parseFloat(value);
                              if (isNaN(num) || num < -180 || num > 180) {
                                return "La longitude doit être entre -180 et 180";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.location_longitude && (
                          <div className="invalid-feedback d-block">{errors.location_longitude.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Description de la localisation</label>
                        <input
                          type="text"
                          className="form-control"
                          name="location_description"
                          defaultValue={formData.location_description}
                          placeholder="Ex: Zone de production, Bâtiment A, etc."
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="immediate_action_taken"
                            defaultChecked={formData.immediate_action_taken}
                            ref={register()}
                          />
                          <label className="form-check-label">Action immédiate prise</label>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Proposition d'amélioration</label>
                        <textarea
                          className="form-control"
                          name="improvement_proposal"
                          rows="3"
                          defaultValue={formData.improvement_proposal}
                          placeholder="Décrire les propositions d'amélioration..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Date prévue de correction</label>
                        <input
                          type="date"
                          className={`form-control ${errors.correction_due_date ? 'is-invalid' : ''}`}
                          name="correction_due_date"
                          defaultValue={formData.correction_due_date}
                          ref={register({
                            validate: (value) => {
                              if (!value || value === "") return true; // Champ optionnel
                              const date = new Date(value);
                              if (isNaN(date.getTime())) {
                                return "Veuillez saisir une date valide";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.correction_due_date && (
                          <div className="invalid-feedback d-block">{errors.correction_due_date.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Coût estimé (FCFA)</label>
                        <input
                          type="number"
                          className={`form-control ${errors.estimated_cost ? 'is-invalid' : ''}`}
                          name="estimated_cost"
                          min="0"
                          step="0.01"
                          defaultValue={formData.estimated_cost}
                          placeholder="0.00"
                          ref={register({
                            validate: (value) => {
                              if (!value || value === "") return true; // Champ optionnel
                              const num = parseFloat(value);
                              if (isNaN(num) || num < 0) {
                                return "Le coût doit être un nombre positif";
                              }
                              return true;
                            }
                          })}
                        />
                        {errors.estimated_cost && (
                          <div className="invalid-feedback d-block">{errors.estimated_cost.message}</div>
                        )}
                      </div>
                    </div>

                    {isEdit && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Statut</label>
                          <select className="form-select" name="status" defaultValue={formData.status}>
                            <option value="submitted">Soumise</option>
                            <option value="received">Reçue</option>
                            <option value="in_analysis">En analyse</option>
                            <option value="corrective_action_requested">Action requise</option>
                            <option value="action_completed">Action réalisée</option>
                            <option value="verified">Vérifiée</option>
                            <option value="closed">Clôturée</option>
                          </select>
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Images/Vidéos (preuve)</label>
                        <div className="d-flex mb-2">
                          <input
                            type="file"
                            className="form-control me-2"
                            multiple
                            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.mp4,.avi,.mov,.wmv,.flv,.webm"
                            onChange={handleFileChange}
                            id="file-input-observation"
                            style={{ flex: 1 }}
                          />
                          <Button
                            type="button"
                            color="primary"
                            outline
                            onClick={() => document.getElementById('file-input-observation').click()}
                          >
                            <Icon name="plus"></Icon>
                            <span>Ajouter plus</span>
                          </Button>
                        </div>
                        {attachments.length > 0 && (
                          <div className="mt-3">
                            <small className="text-muted d-block mb-2">Fichiers sélectionnés ({attachments.length}):</small>
                            <div className="row g-3">
                              {attachments.map((file, index) => {
                                const previewUrl = getFilePreview(file, index);
                                return (
                                  <div key={index} className="col-md-6 col-lg-4">
                                    <div className="card border">
                                      <div className="card-body p-2">
                                        {isImage(file) && previewUrl ? (
                                          <div className="position-relative">
                                            <img
                                              src={previewUrl}
                                              alt={file.name}
                                              className="img-fluid rounded"
                                              style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                            />
                                            <div className="position-absolute top-0 end-0 m-2">
                                              <Badge color="primary">Image</Badge>
                                            </div>
                                          </div>
                                        ) : isVideo(file) && previewUrl ? (
                                          <div className="position-relative">
                                            <video
                                              src={previewUrl}
                                              controls
                                              className="w-100 rounded"
                                              style={{ maxHeight: '150px' }}
                                            />
                                            <div className="position-absolute top-0 end-0 m-2">
                                              <Badge color="danger">Vidéo</Badge>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="text-center p-3">
                                            <Icon name="file" className="fs-1 text-muted"></Icon>
                                            <p className="small mb-0 mt-2">{file.name}</p>
                                          </div>
                                        )}
                                        <div className="mt-2 d-flex align-items-center justify-content-between">
                                          <small className="text-muted text-truncate me-2" title={file.name} style={{ flex: 1 }}>
                                            {file.name}
                                          </small>
                                          <Button
                                            type="button"
                                            color="link"
                                            size="sm"
                                            onClick={() => {
                                              // Nettoyer l'URL de preview si elle existe
                                              if (previewUrls[index]) {
                                                URL.revokeObjectURL(previewUrls[index]);
                                              }
                                              removeAttachment(index);
                                            }}
                                            className="p-0"
                                            style={{ flexShrink: 0 }}
                                          >
                                            <Icon name="cross" className="text-danger" />
                                          </Button>
                                        </div>
                                        <div className="mt-1">
                                          <small className="text-muted">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <small className="text-muted d-block mt-1">
                          Formats acceptés: JPG, JPEG, PNG, GIF, WEBP, PDF, MP4, AVI, MOV, WMV, FLV, WEBM. Taille max: 10MB par fichier, 20MB au total.
                        </small>
                      </div>
                    </div>

                    {loading && attachments.length > 0 && (
                      <div className="col-12">
                        <div className="alert alert-info mb-0">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span>Téléversement des fichiers de preuve...</span>
                            <strong>{uploadProgress}%</strong>
                          </div>
                          <div className="progress" style={{ height: "8px" }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${uploadProgress}%` }}
                              aria-valuenow={uploadProgress}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <div className="form-group">
                        <div className="d-flex">
                          <Button
                            className="me-2"
                            color="light"
                            onClick={() => history.push("/observations")}
                            disabled={loading}
                          >
                            Annuler
                          </Button>
                          <Button
                            color="primary"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                {isEdit ? "Modification..." : "Création..."}
                              </>
                            ) : (
                              <>
                                {isEdit ? "Modifier" : "Créer"} l'observation
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ObservationFormPage;

